import React, { useState, useEffect } from 'react';
import * as araService from '../../services/araService';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import '../../styles/Module.css';
import '../../styles/ARA.css';

const ARAModule = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dashboard data
  const [membresStats, setMembresStats] = useState(null);
  const [cotisationsStats, setCotisationsStats] = useState(null);
  const [prestationsStats, setPrestationsStats] = useState(null);
  const [depensesStats, setDepensesStats] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); // Année en cours par défaut
  const [availableYears, setAvailableYears] = useState([]);

  // Membres data
  const [membres, setMembres] = useState([]);
  const [membresPagination, setMembresPagination] = useState({});
  const [membresSearch, setMembresSearch] = useState('');
  const [membresFilter, setMembresFilter] = useState('active'); // active, inactive, all
  const [selectedMembre, setSelectedMembre] = useState(null);
  const [showMembreForm, setShowMembreForm] = useState(false);
  const [membreFormData, setMembreFormData] = useState({});

  // Cotisations data
  const [cotisations, setCotisations] = useState([]);
  const [cotisationsPagination, setCotisationsPagination] = useState({});
  const [selectedCotisation, setSelectedCotisation] = useState(null);
  const [showCotisationForm, setShowCotisationForm] = useState(false);
  const [cotisationFormData, setCotisationFormData] = useState({});

  // Types data
  const [types, setTypes] = useState([]);
  const [showTypeForm, setShowTypeForm] = useState(false);
  const [typeFormData, setTypeFormData] = useState({});
  const [editingType, setEditingType] = useState(null);

  // Status data
  const [statusData, setStatusData] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // all, a_jour, en_retard
  const [statusSearch, setStatusSearch] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState(null); // Type de cotisation sélectionné

  // Prestations data
  const [prestations, setPrestations] = useState([]);
  const [prestationsPagination, setPrestationsPagination] = useState({});
  const [selectedPrestation, setSelectedPrestation] = useState(null);
  const [showPrestationForm, setShowPrestationForm] = useState(false);
  const [prestationFormData, setPrestationFormData] = useState({});

  // Depenses data
  const [depenses, setDepenses] = useState([]);
  const [depensesPagination, setDepensesPagination] = useState({});
  const [selectedDepense, setSelectedDepense] = useState(null);
  const [showDepenseForm, setShowDepenseForm] = useState(false);
  const [depenseFormData, setDepenseFormData] = useState({});

  // Load data on mount and tab change
  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadDashboard(selectedYear);
    } else if (activeTab === 'membres') {
      loadMembres();
    } else if (activeTab === 'cotisations') {
      loadCotisations();
      loadTypes();
      loadMembresForSelect();
    } else if (activeTab === 'types') {
      loadTypes();
    } else if (activeTab === 'statut') {
      loadStatus();
    } else if (activeTab === 'prestations') {
      loadPrestations();
      loadMembresForSelect();
    } else if (activeTab === 'depenses') {
      loadDepenses();
    }
  }, [activeTab, selectedYear]); // eslint-disable-line react-hooks/exhaustive-deps

  // ==================== STATUT ====================

  const loadStatus = async () => {
    setLoading(true);
    try {
      const data = await araService.getCotisationsStatus();
      setStatusData(data);
      // Sélectionner automatiquement le premier type si aucun n'est sélectionné
      if (data.types && data.types.length > 0 && !selectedTypeId) {
        setSelectedTypeId(data.types[0].id);
      }
    } catch (err) {
      setError('Erreur lors du chargement du statut des cotisations');
    }
    setLoading(false);
  };

  const getFilteredStatusMembres = () => {
    if (!statusData || !selectedTypeId) return [];

    let filtered = statusData.membres;

    // Filtre par statut pour le type sélectionné
    if (statusFilter === 'a_jour') {
      filtered = filtered.filter(m => {
        const cot = m.cotisations.find(c => c.type_id === selectedTypeId);
        return cot && cot.est_a_jour;
      });
    } else if (statusFilter === 'en_retard') {
      filtered = filtered.filter(m => {
        const cot = m.cotisations.find(c => c.type_id === selectedTypeId);
        return cot && !cot.est_a_jour;
      });
    }

    // Filtre par recherche
    if (statusSearch.trim()) {
      const search = statusSearch.toLowerCase().trim();
      filtered = filtered.filter(m =>
        (m.nom && m.nom.toLowerCase().includes(search)) ||
        (m.prenom && m.prenom.toLowerCase().includes(search)) ||
        (m.numero_membre && m.numero_membre.toString().includes(search)) ||
        (m.telephone && m.telephone.includes(search))
      );
    }

    return filtered;
  };

  // Export PDF functions
  const exportStatusPDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4'); // portrait
    const filteredMembres = getFilteredStatusMembres();
    const selectedType = statusData.types.find(t => t.id === selectedTypeId);

    // Title
    doc.setFontSize(16);
    doc.text('Statut des Cotisations - ARA', 14, 15);

    // Date
    doc.setFontSize(10);
    doc.text(`Date: ${new Date().toLocaleDateString('fr-FR')}`, 14, 22);

    // Type sélectionné
    doc.text(`Type: ${selectedType?.libelle} (${Number(selectedType?.montant || 0).toLocaleString()} FCFA)`, 14, 28);

    // Filter info
    const filterText = statusFilter === 'all' ? 'Tous' : statusFilter === 'a_jour' ? 'À jour' : 'En retard';
    doc.text(`Filtre: ${filterText} (${filteredMembres.length} membres)`, 14, 34);

    // Table headers
    const headers = ['CODE', 'Nom', 'Prénom', 'Tél', 'Payé', 'Reste', 'Statut'];

    // Table data
    const data = filteredMembres
      .map(membre => {
        const cotisation = membre.cotisations.find(cot => cot.type_id === selectedTypeId);
        if (!cotisation) return null;

        return [
          membre.numero_membre || '',
          membre.nom || '',
          membre.prenom || '',
          membre.telephone || '',
          `${Number(cotisation.total_paye).toLocaleString()}`,
          `${Number(cotisation.reste_a_payer).toLocaleString()}`,
          cotisation.est_a_jour ? 'À jour' : 'En retard'
        ];
      })
      .filter(row => row !== null);

    // Generate table
    autoTable(doc, {
      head: [headers],
      body: data,
      startY: 40,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [0, 123, 255] },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 35 },
        2: { cellWidth: 35 },
        3: { cellWidth: 25 },
        4: { cellWidth: 25 },
        5: { cellWidth: 25 },
        6: { cellWidth: 25 }
      },
      didParseCell: function(data) {
        // Color status column
        if (data.section === 'body' && data.column.index === 6) {
          if (data.cell.raw === 'À jour') {
            data.cell.styles.fillColor = [212, 237, 218];
            data.cell.styles.textColor = [40, 167, 69];
          } else {
            data.cell.styles.fillColor = [248, 215, 218];
            data.cell.styles.textColor = [220, 53, 69];
          }
        }
      }
    });

    doc.save(`statut_cotisations_${selectedType?.libelle}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportMembresPDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');

    // Title
    doc.setFontSize(16);
    doc.text('Liste des Membres - ARA', 14, 15);

    // Date
    doc.setFontSize(10);
    doc.text(`Date: ${new Date().toLocaleDateString('fr-FR')}`, 14, 22);
    doc.text(`Total: ${membres.length} membres`, 14, 28);

    // Table
    const headers = ['CODE', 'Nom', 'Prénom', 'Téléphone', 'Date adhésion'];
    const data = membres.map(m => [
      m.numero_membre || '',
      m.nom || '',
      m.prenom || '',
      m.telephone || '',
      m.date_adhesion ? new Date(m.date_adhesion).toLocaleDateString('fr-FR') : ''
    ]);

    autoTable(doc, {
      head: [headers],
      body: data,
      startY: 35,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [0, 123, 255] }
    });

    doc.save(`membres_ara_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportCotisationsPDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');

    // Title
    doc.setFontSize(16);
    doc.text('Liste des Cotisations - ARA', 14, 15);

    // Date
    doc.setFontSize(10);
    doc.text(`Date: ${new Date().toLocaleDateString('fr-FR')}`, 14, 22);
    doc.text(`Total: ${cotisations.length} cotisations`, 14, 28);

    // Table
    const headers = ['Date', 'Membre', 'Type', 'Montant'];
    const data = cotisations.map(c => [
      c.date_cotisation ? new Date(c.date_cotisation).toLocaleDateString('fr-FR') : '',
      c.nom_membre || '',
      c.type_cotisation || '',
      `${Number(c.montant).toLocaleString()} FCFA`
    ]);

    autoTable(doc, {
      head: [headers],
      body: data,
      startY: 35,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [0, 123, 255] }
    });

    doc.save(`cotisations_ara_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportDashboardPDF = () => {
    if (!membresStats || !cotisationsStats) return;

    const doc = new jsPDF('p', 'mm', 'a4');

    // Title
    doc.setFontSize(18);
    doc.text('Tableau de Bord - ARA', 14, 20);

    // Date
    doc.setFontSize(10);
    doc.text(`Date: ${new Date().toLocaleDateString('fr-FR')}`, 14, 28);

    // Members statistics
    doc.setFontSize(14);
    doc.text('Statistiques des Membres', 14, 40);

    doc.setFontSize(10);
    let yPos = 48;
    doc.text(`Total Membres: ${membresStats.total}`, 20, yPos);
    yPos += 6;
    doc.text(`Membres avec cotisation: ${membresStats.withCotisation}`, 20, yPos);
    yPos += 6;
    doc.text(`Sans cotisation: ${membresStats.withoutCotisation}`, 20, yPos);
    yPos += 6;
    doc.text(`Nouveaux (1 an): ${membresStats.recentMembers}`, 20, yPos);

    // Cotisation statistics
    yPos += 15;
    doc.setFontSize(14);
    doc.text('Statistiques des Cotisations', 14, yPos);

    yPos += 8;
    doc.setFontSize(10);
    doc.text(`Total Cotisations: ${cotisationsStats.totalCount}`, 20, yPos);
    yPos += 6;
    doc.text(`Montant Total: ${Number(cotisationsStats.totalAmount).toLocaleString()} FCFA`, 20, yPos);

    // Prestations statistics
    if (prestationsStats) {
      yPos += 15;
      doc.setFontSize(14);
      doc.text('Statistiques des Prestations', 14, yPos);

      yPos += 8;
      doc.setFontSize(10);
      doc.text(`Total Prestations: ${prestationsStats.totalCount}`, 20, yPos);
      yPos += 6;
      doc.text(`Montant Total: ${Number(prestationsStats.totalAmount).toLocaleString()} FCFA`, 20, yPos);
    }

    // Depenses statistics
    if (depensesStats) {
      yPos += 15;
      doc.setFontSize(14);
      doc.text('Statistiques des Dépenses', 14, yPos);

      yPos += 8;
      doc.setFontSize(10);
      doc.text(`Total Dépenses: ${depensesStats.totalCount}`, 20, yPos);
      yPos += 6;
      doc.text(`Montant Total: ${Number(depensesStats.totalAmount).toLocaleString()} FCFA`, 20, yPos);
    }

    // Cotisations by type table
    if (cotisationsStats.byType && cotisationsStats.byType.length > 0) {
      yPos += 15;
      doc.setFontSize(14);
      doc.text('Cotisations par Type', 14, yPos);

      const headers = ['Type', 'Nombre', 'Montant Total'];
      const data = cotisationsStats.byType.map(type => [
        type.libelle,
        type.count.toString(),
        `${Number(type.total || 0).toLocaleString()} FCFA`
      ]);

      autoTable(doc, {
        head: [headers],
        body: data,
        startY: yPos + 5,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [0, 123, 255] }
      });

      yPos = doc.lastAutoTable.finalY;
    }

    // Prestations by type table
    if (prestationsStats?.byType && prestationsStats.byType.length > 0) {
      yPos += 15;
      doc.setFontSize(14);
      doc.text('Prestations par Type', 14, yPos);

      const headers = ['Type', 'Nombre', 'Montant Total'];
      const data = prestationsStats.byType.map(type => [
        type.type_prestation,
        type.count.toString(),
        `${Number(type.total || 0).toLocaleString()} FCFA`
      ]);

      autoTable(doc, {
        head: [headers],
        body: data,
        startY: yPos + 5,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [255, 193, 7] }
      });
    }

    doc.save(`tableau_bord_ara_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // ==================== DASHBOARD ====================

  const loadDashboard = async (year = null) => {
    setLoading(true);
    try {
      const [mStats, cStats, pStats, dStats] = await Promise.all([
        araService.getMembresStats(),
        araService.getCotisationsStats(year),
        araService.getPrestationsStats(year),
        araService.getDepensesStats(year)
      ]);
      setMembresStats(mStats);
      setCotisationsStats(cStats);
      setPrestationsStats(pStats);
      setDepensesStats(dStats);

      // Collecter les années disponibles
      const years = new Set();
      if (pStats.availableYears) {
        pStats.availableYears.forEach(y => years.add(y));
      }
      // Ajouter l'année en cours si pas déjà présente
      years.add(new Date().getFullYear());
      setAvailableYears([...years].sort((a, b) => b - a));
    } catch (err) {
      setError('Erreur lors du chargement des statistiques');
    }
    setLoading(false);
  };

  // ==================== MEMBRES ====================

  const loadMembres = async (page = 1, filter = membresFilter) => {
    setLoading(true);
    try {
      const result = await araService.getMembres({
        page,
        limit: 20,
        search: membresSearch,
        isActive: filter
      });
      setMembres(result.data);
      setMembresPagination(result.pagination);
    } catch (err) {
      setError('Erreur lors du chargement des membres');
    }
    setLoading(false);
  };

  const loadMembresForSelect = async () => {
    try {
      const result = await araService.getMembres({ limit: 1000 });
      setMembres(result.data);
    } catch (err) {
      console.error('Erreur chargement membres:', err);
    }
  };

  const handleSearchMembres = (e) => {
    e.preventDefault();
    loadMembres(1);
  };

  const handleCreateMembre = () => {
    setMembreFormData({
      numero_membre: '',
      nom: '',
      prenom: '',
      date_naissance: '',
      lieu_naissance: '',
      domicile: '',
      profession: '',
      telephone: '',
      date_adhesion: new Date().toISOString().split('T')[0],
      personne_contact: '',
      telephone_contact: '',
      is_active: 1,
      motif_inactif: ''
    });
    setSelectedMembre(null);
    setShowMembreForm(true);
  };

  const handleEditMembre = (membre) => {
    setMembreFormData({
      numero_membre: membre.numero_membre || '',
      nom: membre.nom || '',
      prenom: membre.prenom || '',
      date_naissance: membre.date_naissance ? membre.date_naissance.split('T')[0] : '',
      lieu_naissance: membre.lieu_naissance || '',
      domicile: membre.domicile || '',
      profession: membre.profession || '',
      telephone: membre.telephone || '',
      date_adhesion: membre.date_adhesion ? membre.date_adhesion.split('T')[0] : '',
      personne_contact: membre.personne_contact || '',
      telephone_contact: membre.telephone_contact || '',
      is_active: membre.is_active !== undefined ? membre.is_active : 1,
      motif_inactif: membre.motif_inactif || ''
    });
    setSelectedMembre(membre);
    setShowMembreForm(true);
  };

  const handleSaveMembre = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (selectedMembre) {
        await araService.updateMembre(selectedMembre.id, membreFormData);
        setSuccess('Membre mis à jour avec succès');
      } else {
        await araService.createMembre(membreFormData);
        setSuccess('Membre créé avec succès');
      }
      setShowMembreForm(false);
      loadMembres();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    }
    setLoading(false);
  };

  const handleDeleteMembre = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce membre ?')) return;
    setLoading(true);
    try {
      await araService.deleteMembre(id);
      setSuccess('Membre supprimé avec succès');
      loadMembres();
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
    setLoading(false);
  };

  // ==================== COTISATIONS ====================

  const loadCotisations = async (page = 1) => {
    setLoading(true);
    try {
      const result = await araService.getCotisations({ page, limit: 20 });
      setCotisations(result.data);
      setCotisationsPagination(result.pagination);
    } catch (err) {
      setError('Erreur lors du chargement des cotisations');
    }
    setLoading(false);
  };

  const handleCreateCotisation = () => {
    setCotisationFormData({
      id_membre: '',
      montant: '',
      date_cotisation: new Date().toISOString().split('T')[0],
      id_type_cotisation: ''
    });
    setSelectedCotisation(null);
    setShowCotisationForm(true);
  };

  const handleEditCotisation = (cotisation) => {
    setCotisationFormData({
      id_membre: cotisation.id_membre,
      montant: cotisation.montant,
      date_cotisation: cotisation.date_cotisation ? cotisation.date_cotisation.split('T')[0] : '',
      id_type_cotisation: cotisation.id_type_cotisation
    });
    setSelectedCotisation(cotisation);
    setShowCotisationForm(true);
  };

  const handleSaveCotisation = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (selectedCotisation) {
        await araService.updateCotisation(selectedCotisation.id, cotisationFormData);
        setSuccess('Cotisation mise à jour avec succès');
      } else {
        await araService.createCotisation(cotisationFormData);
        setSuccess('Cotisation créée avec succès');
      }
      setShowCotisationForm(false);
      loadCotisations();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    }
    setLoading(false);
  };

  const handleDeleteCotisation = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette cotisation ?')) return;
    setLoading(true);
    try {
      await araService.deleteCotisation(id);
      setSuccess('Cotisation supprimée avec succès');
      loadCotisations();
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
    setLoading(false);
  };

  // ==================== TYPES ====================

  const loadTypes = async () => {
    try {
      const data = await araService.getTypesCotisation();
      setTypes(data);
    } catch (err) {
      setError('Erreur lors du chargement des types');
    }
  };

  const handleCreateType = () => {
    setTypeFormData({ libelle: '', montant: '', observation: '' });
    setEditingType(null);
    setShowTypeForm(true);
  };

  const handleEditType = (type) => {
    setTypeFormData({
      libelle: type.libelle,
      montant: type.montant || '',
      observation: type.observation || ''
    });
    setEditingType(type);
    setShowTypeForm(true);
  };

  const handleSaveType = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingType) {
        await araService.updateTypeCotisation(editingType.id, typeFormData);
        setSuccess('Type mis à jour avec succès');
      } else {
        await araService.createTypeCotisation(typeFormData);
        setSuccess('Type créé avec succès');
      }
      setShowTypeForm(false);
      loadTypes();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    }
    setLoading(false);
  };

  const handleDeleteType = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce type ?')) return;
    setLoading(true);
    try {
      await araService.deleteTypeCotisation(id);
      setSuccess('Type supprimé avec succès');
      loadTypes();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
    setLoading(false);
  };

  // ==================== PRESTATIONS ====================

  const loadPrestations = async (page = 1) => {
    setLoading(true);
    try {
      const result = await araService.getPrestations({ page, limit: 20 });
      setPrestations(result.data);
      setPrestationsPagination(result.pagination);
    } catch (err) {
      setError('Erreur lors du chargement des prestations');
    }
    setLoading(false);
  };

  const handleCreatePrestation = () => {
    setPrestationFormData({
      id_membre: '',
      type_prestation: 'Décès',
      montant: '',
      date_prestation: new Date().toISOString().split('T')[0],
      description: ''
    });
    setSelectedPrestation(null);
    setShowPrestationForm(true);
  };

  const handleEditPrestation = (prestation) => {
    setPrestationFormData({
      id_membre: prestation.id_membre,
      type_prestation: prestation.type_prestation,
      montant: prestation.montant,
      date_prestation: prestation.date_prestation ? prestation.date_prestation.split('T')[0] : '',
      description: prestation.description || ''
    });
    setSelectedPrestation(prestation);
    setShowPrestationForm(true);
  };

  const handleSavePrestation = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (selectedPrestation) {
        await araService.updatePrestation(selectedPrestation.id, prestationFormData);
        setSuccess('Prestation mise à jour avec succès');
      } else {
        await araService.createPrestation(prestationFormData);
        setSuccess('Prestation créée avec succès');
      }
      setShowPrestationForm(false);
      loadPrestations();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    }
    setLoading(false);
  };

  const handleDeletePrestation = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette prestation ?')) return;
    setLoading(true);
    try {
      await araService.deletePrestation(id);
      setSuccess('Prestation supprimée avec succès');
      loadPrestations();
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
    setLoading(false);
  };

  const exportPrestationsPDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    doc.setFontSize(16);
    doc.text('Liste des Prestations - ARA', 14, 15);
    doc.setFontSize(10);
    doc.text(`Date: ${new Date().toLocaleDateString('fr-FR')}`, 14, 22);

    const headers = ['Date', 'Membre', 'Type', 'Montant', 'Description'];
    const data = prestations.map(p => [
      p.date_prestation ? new Date(p.date_prestation).toLocaleDateString('fr-FR') : '',
      p.nom_membre || '',
      p.type_prestation || '',
      `${Number(p.montant).toLocaleString()} FCFA`,
      p.description || ''
    ]);

    autoTable(doc, {
      head: [headers],
      body: data,
      startY: 30,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [0, 123, 255] }
    });

    doc.save(`prestations_ara_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // ==================== DEPENSES ====================

  const loadDepenses = async (page = 1) => {
    setLoading(true);
    try {
      const result = await araService.getDepenses({ page, limit: 20 });
      setDepenses(result.data);
      setDepensesPagination(result.pagination);
    } catch (err) {
      setError('Erreur lors du chargement des dépenses');
    }
    setLoading(false);
  };

  const handleCreateDepense = () => {
    setDepenseFormData({
      libelle: '',
      montant: '',
      date_depense: new Date().toISOString().split('T')[0],
      description: ''
    });
    setSelectedDepense(null);
    setShowDepenseForm(true);
  };

  const handleEditDepense = (depense) => {
    setDepenseFormData({
      libelle: depense.libelle,
      montant: depense.montant,
      date_depense: depense.date_depense ? depense.date_depense.split('T')[0] : '',
      description: depense.description || ''
    });
    setSelectedDepense(depense);
    setShowDepenseForm(true);
  };

  const handleSaveDepense = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (selectedDepense) {
        await araService.updateDepense(selectedDepense.id, depenseFormData);
        setSuccess('Dépense mise à jour avec succès');
      } else {
        await araService.createDepense(depenseFormData);
        setSuccess('Dépense créée avec succès');
      }
      setShowDepenseForm(false);
      loadDepenses();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    }
    setLoading(false);
  };

  const handleDeleteDepense = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette dépense ?')) return;
    setLoading(true);
    try {
      await araService.deleteDepense(id);
      setSuccess('Dépense supprimée avec succès');
      loadDepenses();
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
    setLoading(false);
  };

  const exportDepensesPDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    doc.setFontSize(16);
    doc.text('Liste des Dépenses - ARA', 14, 15);
    doc.setFontSize(10);
    doc.text(`Date: ${new Date().toLocaleDateString('fr-FR')}`, 14, 22);

    const headers = ['Date', 'Libellé', 'Montant', 'Description'];
    const data = depenses.map(d => [
      d.date_depense ? new Date(d.date_depense).toLocaleDateString('fr-FR') : '',
      d.libelle || '',
      `${Number(d.montant).toLocaleString()} FCFA`,
      d.description || ''
    ]);

    autoTable(doc, {
      head: [headers],
      body: data,
      startY: 30,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [0, 123, 255] }
    });

    doc.save(`depenses_ara_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // Clear messages after delay
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  // ==================== RENDER ====================

  return (
    <div className="ara-module">
      <h1>Module ARA - Gestion de l'Association</h1>

      {/* Messages */}
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Tabs */}
      <div className="ara-tabs">
        <button
          className={activeTab === 'dashboard' ? 'active' : ''}
          onClick={() => setActiveTab('dashboard')}
        >
          Tableau de bord
        </button>
        <button
          className={activeTab === 'membres' ? 'active' : ''}
          onClick={() => setActiveTab('membres')}
        >
          Membres
        </button>
        <button
          className={activeTab === 'cotisations' ? 'active' : ''}
          onClick={() => setActiveTab('cotisations')}
        >
          Cotisations
        </button>
        <button
          className={activeTab === 'types' ? 'active' : ''}
          onClick={() => setActiveTab('types')}
        >
          Types de cotisation
        </button>
        <button
          className={activeTab === 'statut' ? 'active' : ''}
          onClick={() => setActiveTab('statut')}
        >
          Statut des paiements
        </button>
        <button
          className={activeTab === 'prestations' ? 'active' : ''}
          onClick={() => setActiveTab('prestations')}
        >
          Prestations
        </button>
        <button
          className={activeTab === 'depenses' ? 'active' : ''}
          onClick={() => setActiveTab('depenses')}
        >
          Dépenses
        </button>
      </div>

      {/* Content */}
      <div className="ara-content">
        {loading && <div className="loading">Chargement...</div>}

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && !loading && (
          <div className="dashboard">
            <div className="section-header" style={{justifyContent: 'space-between', marginBottom: '20px'}}>
              <h2 style={{margin: 0}}>Tableau de Bord ARA</h2>
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <label style={{fontWeight: 'bold'}}>Année:</label>
                <select
                  value={selectedYear || ''}
                  onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : null)}
                  style={{padding: '8px 12px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px', minWidth: '150px'}}
                >
                  <option value={new Date().getFullYear()}>{new Date().getFullYear()} (en cours)</option>
                  {availableYears
                    .filter(year => year !== new Date().getFullYear())
                    .map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))
                  }
                  <option value="">Toutes les années</option>
                </select>
                <button className="btn-export" onClick={exportDashboardPDF}>
                  Export PDF
                </button>
              </div>
            </div>
            <div className="stats-grid">
              {membresStats && (
                <>
                  <div className="stat-card">
                    <h3>Membres Actifs</h3>
                    <p className="stat-value">{membresStats.total}</p>
                  </div>
                  <div className="stat-card" style={{background: '#f8d7da', borderColor: '#dc3545'}}>
                    <h3>Membres Inactifs</h3>
                    <p className="stat-value" style={{color: '#dc3545'}}>{membresStats.totalInactif || 0}</p>
                  </div>
                  <div className="stat-card">
                    <h3>Avec cotisation</h3>
                    <p className="stat-value">{membresStats.withCotisation}</p>
                  </div>
                  <div className="stat-card">
                    <h3>Nouveaux (1 an)</h3>
                    <p className="stat-value">{membresStats.recentMembers}</p>
                  </div>
                </>
              )}
              {cotisationsStats && (
                <>
                  <div className="stat-card highlight">
                    <h3>Total Cotisations</h3>
                    <p className="stat-value">{cotisationsStats.totalCount}</p>
                  </div>
                  <div className="stat-card highlight">
                    <h3>Montant Cotisations</h3>
                    <p className="stat-value">{Number(cotisationsStats.totalAmount).toLocaleString()} FCFA</p>
                  </div>
                </>
              )}
              {prestationsStats && (
                <>
                  <div className="stat-card" style={{background: '#fff3cd', borderColor: '#ffc107'}}>
                    <h3>Total Prestations</h3>
                    <p className="stat-value">{prestationsStats.totalCount}</p>
                  </div>
                  <div className="stat-card" style={{background: '#fff3cd', borderColor: '#ffc107'}}>
                    <h3>Montant Prestations</h3>
                    <p className="stat-value" style={{color: '#856404'}}>{Number(prestationsStats.totalAmount).toLocaleString()} FCFA</p>
                  </div>
                </>
              )}
              {depensesStats && (
                <>
                  <div className="stat-card" style={{background: '#f8d7da', borderColor: '#dc3545'}}>
                    <h3>Total Dépenses</h3>
                    <p className="stat-value">{depensesStats.totalCount}</p>
                  </div>
                  <div className="stat-card" style={{background: '#f8d7da', borderColor: '#dc3545'}}>
                    <h3>Montant Dépenses</h3>
                    <p className="stat-value" style={{color: '#dc3545'}}>{Number(depensesStats.totalAmount).toLocaleString()} FCFA</p>
                  </div>
                </>
              )}
            </div>

            {cotisationsStats?.byType && (
              <div className="stats-section">
                <h3>Cotisations par type</h3>
                <table className="ara-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Nombre</th>
                      <th>Montant Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cotisationsStats.byType.map((type) => (
                      <tr key={type.id}>
                        <td>{type.libelle}</td>
                        <td>{type.count}</td>
                        <td>{Number(type.total || 0).toLocaleString()} FCFA</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {prestationsStats?.byType && prestationsStats.byType.length > 0 && (
              <div className="stats-section">
                <h3>Prestations par type</h3>
                <table className="ara-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Nombre</th>
                      <th>Montant Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prestationsStats.byType.map((type, index) => (
                      <tr key={index}>
                        <td>{type.type_prestation}</td>
                        <td>{type.count}</td>
                        <td>{Number(type.total || 0).toLocaleString()} FCFA</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {membresStats?.byMotifInactif && membresStats.byMotifInactif.length > 0 && (
              <div className="stats-section">
                <h3>Membres inactifs par motif</h3>
                <table className="ara-table">
                  <thead>
                    <tr>
                      <th>Motif</th>
                      <th>Nombre</th>
                    </tr>
                  </thead>
                  <tbody>
                    {membresStats.byMotifInactif.map((motif, index) => (
                      <tr key={index}>
                        <td>{motif.motif_inactif}</td>
                        <td>{motif.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Membres Tab */}
        {activeTab === 'membres' && !loading && (
          <div className="membres-section">
            <div className="section-header">
              <form onSubmit={handleSearchMembres} className="search-form">
                <input
                  type="text"
                  placeholder="Rechercher un membre..."
                  value={membresSearch}
                  onChange={(e) => setMembresSearch(e.target.value)}
                />
                <button type="submit">Rechercher</button>
              </form>
              <div className="filter-buttons">
                <button
                  className={membresFilter === 'active' ? 'active' : ''}
                  onClick={() => { setMembresFilter('active'); loadMembres(1, 'active'); }}
                >
                  Actifs
                </button>
                <button
                  className={membresFilter === 'inactive' ? 'active' : ''}
                  onClick={() => { setMembresFilter('inactive'); loadMembres(1, 'inactive'); }}
                  style={{background: membresFilter === 'inactive' ? '#dc3545' : ''}}
                >
                  Inactifs
                </button>
                <button
                  className={membresFilter === 'all' ? 'active' : ''}
                  onClick={() => { setMembresFilter('all'); loadMembres(1, 'all'); }}
                >
                  Tous
                </button>
              </div>
              <div style={{display: 'flex', gap: '10px'}}>
                <button className="btn-export" onClick={exportMembresPDF}>
                  Export PDF
                </button>
                <button className="btn-primary" onClick={handleCreateMembre}>
                  + Nouveau membre
                </button>
              </div>
            </div>

            <table className="ara-table">
              <thead>
                <tr>
                  <th>CODE ARA</th>
                  <th>Nom</th>
                  <th>Prénom</th>
                  <th>Téléphone</th>
                  <th>Date adhésion</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {membres.map((membre) => (
                  <tr key={membre.id} style={{opacity: membre.is_active ? 1 : 0.7}}>
                    <td>{membre.numero_membre}</td>
                    <td>{membre.nom}</td>
                    <td>{membre.prenom}</td>
                    <td>{membre.telephone}</td>
                    <td>{membre.date_adhesion ? new Date(membre.date_adhesion).toLocaleDateString('fr-FR') : '-'}</td>
                    <td>
                      {membre.is_active ? (
                        <span style={{color: '#28a745', fontWeight: 'bold'}}>Actif</span>
                      ) : (
                        <span style={{color: '#dc3545', fontWeight: 'bold'}}>
                          {membre.motif_inactif || 'Inactif'}
                        </span>
                      )}
                    </td>
                    <td className="actions">
                      <button onClick={() => handleEditMembre(membre)}>Modifier</button>
                      {membre.is_active && (
                        <button className="btn-danger" onClick={() => handleDeleteMembre(membre.id)}>Supprimer</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {membresPagination.totalPages > 1 && (
              <div className="pagination">
                <button
                  disabled={membresPagination.page === 1}
                  onClick={() => loadMembres(1)}
                >
                  ««
                </button>
                <button
                  disabled={membresPagination.page === 1}
                  onClick={() => loadMembres(membresPagination.page - 1)}
                >
                  «
                </button>
                {Array.from({ length: Math.min(5, membresPagination.totalPages) }, (_, i) => {
                  let pageNum;
                  if (membresPagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (membresPagination.page <= 3) {
                    pageNum = i + 1;
                  } else if (membresPagination.page >= membresPagination.totalPages - 2) {
                    pageNum = membresPagination.totalPages - 4 + i;
                  } else {
                    pageNum = membresPagination.page - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      className={membresPagination.page === pageNum ? 'active' : ''}
                      onClick={() => loadMembres(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  disabled={membresPagination.page === membresPagination.totalPages}
                  onClick={() => loadMembres(membresPagination.page + 1)}
                >
                  »
                </button>
                <button
                  disabled={membresPagination.page === membresPagination.totalPages}
                  onClick={() => loadMembres(membresPagination.totalPages)}
                >
                  »»
                </button>
                <span className="pagination-info">
                  Page {membresPagination.page} / {membresPagination.totalPages} ({membresPagination.total} membres)
                </span>
              </div>
            )}
          </div>
        )}

        {/* Cotisations Tab */}
        {activeTab === 'cotisations' && !loading && (
          <div className="cotisations-section">
            <div className="section-header">
              <button className="btn-export" onClick={exportCotisationsPDF}>
                Export PDF
              </button>
              <button className="btn-primary" onClick={handleCreateCotisation}>
                + Nouvelle cotisation
              </button>
            </div>

            <table className="ara-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Membre</th>
                  <th>Type</th>
                  <th>Montant</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {cotisations.map((cotisation) => (
                  <tr key={cotisation.id}>
                    <td>{cotisation.date_cotisation ? new Date(cotisation.date_cotisation).toLocaleDateString('fr-FR') : '-'}</td>
                    <td>{cotisation.nom_membre}</td>
                    <td>{cotisation.type_cotisation}</td>
                    <td>{Number(cotisation.montant).toLocaleString()} FCFA</td>
                    <td className="actions">
                      <button onClick={() => handleEditCotisation(cotisation)}>Modifier</button>
                      <button className="btn-danger" onClick={() => handleDeleteCotisation(cotisation.id)}>Supprimer</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {cotisationsPagination.totalPages > 1 && (
              <div className="pagination">
                <button
                  disabled={cotisationsPagination.page === 1}
                  onClick={() => loadCotisations(1)}
                >
                  ««
                </button>
                <button
                  disabled={cotisationsPagination.page === 1}
                  onClick={() => loadCotisations(cotisationsPagination.page - 1)}
                >
                  «
                </button>
                {Array.from({ length: Math.min(5, cotisationsPagination.totalPages) }, (_, i) => {
                  let pageNum;
                  if (cotisationsPagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (cotisationsPagination.page <= 3) {
                    pageNum = i + 1;
                  } else if (cotisationsPagination.page >= cotisationsPagination.totalPages - 2) {
                    pageNum = cotisationsPagination.totalPages - 4 + i;
                  } else {
                    pageNum = cotisationsPagination.page - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      className={cotisationsPagination.page === pageNum ? 'active' : ''}
                      onClick={() => loadCotisations(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  disabled={cotisationsPagination.page === cotisationsPagination.totalPages}
                  onClick={() => loadCotisations(cotisationsPagination.page + 1)}
                >
                  »
                </button>
                <button
                  disabled={cotisationsPagination.page === cotisationsPagination.totalPages}
                  onClick={() => loadCotisations(cotisationsPagination.totalPages)}
                >
                  »»
                </button>
                <span className="pagination-info">
                  Page {cotisationsPagination.page} / {cotisationsPagination.totalPages} ({cotisationsPagination.total} cotisations)
                </span>
              </div>
            )}
          </div>
        )}

        {/* Types Tab */}
        {activeTab === 'types' && !loading && (
          <div className="types-section">
            <div className="section-header">
              <button className="btn-primary" onClick={handleCreateType}>
                + Nouveau type
              </button>
            </div>

            <table className="ara-table">
              <thead>
                <tr>
                  <th>Libellé</th>
                  <th>Montant par défaut</th>
                  <th>Observation</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {types.map((type) => (
                  <tr key={type.id}>
                    <td>{type.libelle}</td>
                    <td>{Number(type.montant || 0).toLocaleString()} FCFA</td>
                    <td>{type.observation || '-'}</td>
                    <td className="actions">
                      <button onClick={() => handleEditType(type)}>Modifier</button>
                      <button className="btn-danger" onClick={() => handleDeleteType(type.id)}>Supprimer</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Statut Tab */}
        {activeTab === 'statut' && !loading && statusData && selectedTypeId && (
          <div className="statut-section">
            {/* Summary */}
            <div className="stats-grid" style={{marginBottom: '20px'}}>
              <div className="stat-card">
                <h3>Total Membres</h3>
                <p className="stat-value">{statusData.summary.total_membres}</p>
              </div>
              <div className="stat-card" style={{background: '#d4edda', borderColor: '#28a745'}}>
                <h3>À jour</h3>
                <p className="stat-value" style={{color: '#28a745'}}>
                  {statusData.membres.filter(m => {
                    const cot = m.cotisations.find(c => c.type_id === selectedTypeId);
                    return cot && cot.est_a_jour;
                  }).length}
                </p>
              </div>
              <div className="stat-card" style={{background: '#f8d7da', borderColor: '#dc3545'}}>
                <h3>En retard</h3>
                <p className="stat-value" style={{color: '#dc3545'}}>
                  {statusData.membres.filter(m => {
                    const cot = m.cotisations.find(c => c.type_id === selectedTypeId);
                    return cot && !cot.est_a_jour;
                  }).length}
                </p>
              </div>
            </div>

            {/* Search and Filter */}
            <div className="section-header">
              <div className="search-form">
                <input
                  type="text"
                  placeholder="Rechercher (nom, code, téléphone)..."
                  value={statusSearch}
                  onChange={(e) => setStatusSearch(e.target.value)}
                  style={{minWidth: '250px'}}
                />
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <label style={{fontWeight: 'bold'}}>Type de cotisation:</label>
                <select
                  value={selectedTypeId || ''}
                  onChange={(e) => setSelectedTypeId(parseInt(e.target.value))}
                  style={{padding: '8px 12px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px', minWidth: '200px'}}
                >
                  {statusData.types.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.libelle} ({Number(type.montant).toLocaleString()} FCFA)
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-buttons">
                <button
                  className={statusFilter === 'all' ? 'active' : ''}
                  onClick={() => setStatusFilter('all')}
                >
                  Tous ({statusData.summary.total_membres})
                </button>
                <button
                  className={statusFilter === 'a_jour' ? 'active' : ''}
                  onClick={() => setStatusFilter('a_jour')}
                  style={{background: statusFilter === 'a_jour' ? '#28a745' : ''}}
                >
                  À jour ({statusData.membres.filter(m => {
                    const cot = m.cotisations.find(c => c.type_id === selectedTypeId);
                    return cot && cot.est_a_jour;
                  }).length})
                </button>
                <button
                  className={statusFilter === 'en_retard' ? 'active' : ''}
                  onClick={() => setStatusFilter('en_retard')}
                  style={{background: statusFilter === 'en_retard' ? '#dc3545' : ''}}
                >
                  En retard ({statusData.membres.filter(m => {
                    const cot = m.cotisations.find(c => c.type_id === selectedTypeId);
                    return cot && !cot.est_a_jour;
                  }).length})
                </button>
              </div>
              <button className="btn-export" onClick={exportStatusPDF}>
                Export PDF
              </button>
            </div>

            {/* Table */}
            <table className="ara-table">
              <thead>
                <tr>
                  <th>CODE ARA</th>
                  <th>Nom</th>
                  <th>Téléphone</th>
                  <th>Montant Payé</th>
                  <th>Reste à Payer</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredStatusMembres().map((membre) => {
                  // Trouver la cotisation du type sélectionné
                  const cotisation = membre.cotisations.find(cot => cot.type_id === selectedTypeId);
                  if (!cotisation) return null;

                  return (
                    <tr key={membre.membre_id}>
                      <td>{membre.numero_membre}</td>
                      <td>{membre.nom} {membre.prenom}</td>
                      <td>{membre.telephone || '-'}</td>
                      <td style={{
                        background: cotisation.est_a_jour ? '#d4edda' : '#f8d7da',
                        textAlign: 'center',
                        fontWeight: 'bold'
                      }}>
                        {Number(cotisation.total_paye).toLocaleString()} FCFA
                      </td>
                      <td style={{
                        textAlign: 'center',
                        color: cotisation.reste_a_payer > 0 ? '#dc3545' : '#28a745',
                        fontWeight: 'bold'
                      }}>
                        {Number(cotisation.reste_a_payer).toLocaleString()} FCFA
                      </td>
                      <td>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          background: cotisation.est_a_jour ? '#28a745' : '#dc3545',
                          color: 'white'
                        }}>
                          {cotisation.est_a_jour ? 'À jour' : 'En retard'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Prestations Tab */}
        {activeTab === 'prestations' && !loading && (
          <div className="prestations-section">
            <div className="section-header">
              <button className="btn-export" onClick={exportPrestationsPDF}>
                Export PDF
              </button>
              <button className="btn-primary" onClick={handleCreatePrestation}>
                + Nouvelle prestation
              </button>
            </div>

            <table className="ara-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Membre</th>
                  <th>Type</th>
                  <th>Montant</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {prestations.map((prestation) => (
                  <tr key={prestation.id}>
                    <td>{prestation.date_prestation ? new Date(prestation.date_prestation).toLocaleDateString('fr-FR') : '-'}</td>
                    <td>{prestation.numero_membre} - {prestation.nom_membre}</td>
                    <td>{prestation.type_prestation}</td>
                    <td>{Number(prestation.montant).toLocaleString()} FCFA</td>
                    <td>{prestation.description || '-'}</td>
                    <td className="actions">
                      <button onClick={() => handleEditPrestation(prestation)}>Modifier</button>
                      <button className="btn-danger" onClick={() => handleDeletePrestation(prestation.id)}>Supprimer</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {prestationsPagination.totalPages > 1 && (
              <div className="pagination">
                <button disabled={prestationsPagination.page === 1} onClick={() => loadPrestations(1)}>««</button>
                <button disabled={prestationsPagination.page === 1} onClick={() => loadPrestations(prestationsPagination.page - 1)}>«</button>
                {Array.from({ length: Math.min(5, prestationsPagination.totalPages) }, (_, i) => {
                  let pageNum = prestationsPagination.totalPages <= 5 ? i + 1 :
                    prestationsPagination.page <= 3 ? i + 1 :
                    prestationsPagination.page >= prestationsPagination.totalPages - 2 ? prestationsPagination.totalPages - 4 + i :
                    prestationsPagination.page - 2 + i;
                  return (
                    <button key={pageNum} className={prestationsPagination.page === pageNum ? 'active' : ''} onClick={() => loadPrestations(pageNum)}>
                      {pageNum}
                    </button>
                  );
                })}
                <button disabled={prestationsPagination.page === prestationsPagination.totalPages} onClick={() => loadPrestations(prestationsPagination.page + 1)}>»</button>
                <button disabled={prestationsPagination.page === prestationsPagination.totalPages} onClick={() => loadPrestations(prestationsPagination.totalPages)}>»»</button>
                <span className="pagination-info">
                  Page {prestationsPagination.page} / {prestationsPagination.totalPages} ({prestationsPagination.total} prestations)
                </span>
              </div>
            )}
          </div>
        )}

        {/* Depenses Tab */}
        {activeTab === 'depenses' && !loading && (
          <div className="depenses-section">
            <div className="section-header">
              <button className="btn-export" onClick={exportDepensesPDF}>
                Export PDF
              </button>
              <button className="btn-primary" onClick={handleCreateDepense}>
                + Nouvelle dépense
              </button>
            </div>

            <table className="ara-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Libellé</th>
                  <th>Montant</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {depenses.map((depense) => (
                  <tr key={depense.id}>
                    <td>{depense.date_depense ? new Date(depense.date_depense).toLocaleDateString('fr-FR') : '-'}</td>
                    <td>{depense.libelle}</td>
                    <td>{Number(depense.montant).toLocaleString()} FCFA</td>
                    <td>{depense.description || '-'}</td>
                    <td className="actions">
                      <button onClick={() => handleEditDepense(depense)}>Modifier</button>
                      <button className="btn-danger" onClick={() => handleDeleteDepense(depense.id)}>Supprimer</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {depensesPagination.totalPages > 1 && (
              <div className="pagination">
                <button disabled={depensesPagination.page === 1} onClick={() => loadDepenses(1)}>««</button>
                <button disabled={depensesPagination.page === 1} onClick={() => loadDepenses(depensesPagination.page - 1)}>«</button>
                {Array.from({ length: Math.min(5, depensesPagination.totalPages) }, (_, i) => {
                  let pageNum = depensesPagination.totalPages <= 5 ? i + 1 :
                    depensesPagination.page <= 3 ? i + 1 :
                    depensesPagination.page >= depensesPagination.totalPages - 2 ? depensesPagination.totalPages - 4 + i :
                    depensesPagination.page - 2 + i;
                  return (
                    <button key={pageNum} className={depensesPagination.page === pageNum ? 'active' : ''} onClick={() => loadDepenses(pageNum)}>
                      {pageNum}
                    </button>
                  );
                })}
                <button disabled={depensesPagination.page === depensesPagination.totalPages} onClick={() => loadDepenses(depensesPagination.page + 1)}>»</button>
                <button disabled={depensesPagination.page === depensesPagination.totalPages} onClick={() => loadDepenses(depensesPagination.totalPages)}>»»</button>
                <span className="pagination-info">
                  Page {depensesPagination.page} / {depensesPagination.totalPages} ({depensesPagination.total} dépenses)
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Membre Form Modal */}
      {showMembreForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{selectedMembre ? 'Modifier le membre' : 'Nouveau membre'}</h2>
            <form onSubmit={handleSaveMembre}>
              <div className="form-row">
                <div className="form-group">
                  <label>CODE ARA *</label>
                  <input
                    type="number"
                    required
                    value={membreFormData.numero_membre}
                    onChange={(e) => setMembreFormData({ ...membreFormData, numero_membre: e.target.value })}
                    disabled={selectedMembre !== null}
                  />
                  {selectedMembre && <small style={{color: '#666'}}>Non modifiable</small>}
                </div>
                <div className="form-group">
                  <label>Nom *</label>
                  <input
                    type="text"
                    required
                    value={membreFormData.nom}
                    onChange={(e) => setMembreFormData({ ...membreFormData, nom: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Prénom</label>
                  <input
                    type="text"
                    value={membreFormData.prenom}
                    onChange={(e) => setMembreFormData({ ...membreFormData, prenom: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row two-cols">
                <div className="form-group">
                  <label>Date de naissance</label>
                  <input
                    type="date"
                    value={membreFormData.date_naissance}
                    onChange={(e) => setMembreFormData({ ...membreFormData, date_naissance: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Lieu de naissance</label>
                  <input
                    type="text"
                    value={membreFormData.lieu_naissance}
                    onChange={(e) => setMembreFormData({ ...membreFormData, lieu_naissance: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row two-cols">
                <div className="form-group">
                  <label>Domicile</label>
                  <input
                    type="text"
                    value={membreFormData.domicile}
                    onChange={(e) => setMembreFormData({ ...membreFormData, domicile: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Date d'adhésion</label>
                  <input
                    type="date"
                    value={membreFormData.date_adhesion}
                    onChange={(e) => setMembreFormData({ ...membreFormData, date_adhesion: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row two-cols">
                <div className="form-group">
                  <label>Profession</label>
                  <input
                    type="text"
                    value={membreFormData.profession}
                    onChange={(e) => setMembreFormData({ ...membreFormData, profession: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Téléphone</label>
                  <input
                    type="text"
                    value={membreFormData.telephone}
                    onChange={(e) => setMembreFormData({ ...membreFormData, telephone: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row two-cols">
                <div className="form-group">
                  <label>Personne à contacter</label>
                  <input
                    type="text"
                    value={membreFormData.personne_contact}
                    onChange={(e) => setMembreFormData({ ...membreFormData, personne_contact: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Téléphone contact</label>
                  <input
                    type="text"
                    value={membreFormData.telephone_contact}
                    onChange={(e) => setMembreFormData({ ...membreFormData, telephone_contact: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row two-cols">
                <div className="form-group">
                  <label>Statut</label>
                  <select
                    value={membreFormData.is_active}
                    onChange={(e) => setMembreFormData({
                      ...membreFormData,
                      is_active: parseInt(e.target.value),
                      motif_inactif: e.target.value === '1' ? '' : membreFormData.motif_inactif
                    })}
                  >
                    <option value={1}>Actif</option>
                    <option value={0}>Inactif</option>
                  </select>
                </div>
                {membreFormData.is_active === 0 && (
                  <div className="form-group">
                    <label>Motif d'inactivité *</label>
                    <select
                      required
                      value={membreFormData.motif_inactif}
                      onChange={(e) => setMembreFormData({ ...membreFormData, motif_inactif: e.target.value })}
                    >
                      <option value="">Sélectionner un motif</option>
                      <option value="Décès">Décès</option>
                      <option value="Démission">Démission</option>
                      <option value="Suspension">Suspension</option>
                      <option value="Radiation">Radiation</option>
                    </select>
                  </div>
                )}
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowMembreForm(false)}>Annuler</button>
                <button type="submit" className="btn-primary">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cotisation Form Modal */}
      {showCotisationForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{selectedCotisation ? 'Modifier la cotisation' : 'Nouvelle cotisation'}</h2>
            <form onSubmit={handleSaveCotisation}>
              <div className="form-group">
                <label>Membre *</label>
                <select
                  required
                  value={cotisationFormData.id_membre}
                  onChange={(e) => setCotisationFormData({ ...cotisationFormData, id_membre: e.target.value })}
                >
                  <option value="">Sélectionner un membre</option>
                  {membres.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.numero_membre} - {m.nom} {m.prenom}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Type de cotisation *</label>
                <select
                  required
                  value={cotisationFormData.id_type_cotisation}
                  onChange={(e) => {
                    const typeId = e.target.value;
                    const type = types.find(t => t.id === parseInt(typeId));
                    setCotisationFormData({
                      ...cotisationFormData,
                      id_type_cotisation: typeId,
                      montant: type ? type.montant : cotisationFormData.montant
                    });
                  }}
                >
                  <option value="">Sélectionner un type</option>
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>{t.libelle}</option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Montant (FCFA) *</label>
                  <input
                    type="number"
                    required
                    value={cotisationFormData.montant}
                    onChange={(e) => setCotisationFormData({ ...cotisationFormData, montant: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    required
                    value={cotisationFormData.date_cotisation}
                    onChange={(e) => setCotisationFormData({ ...cotisationFormData, date_cotisation: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowCotisationForm(false)}>Annuler</button>
                <button type="submit" className="btn-primary">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Type Form Modal */}
      {showTypeForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{editingType ? 'Modifier le type' : 'Nouveau type de cotisation'}</h2>
            <form onSubmit={handleSaveType}>
              <div className="form-group">
                <label>Libellé *</label>
                <input
                  type="text"
                  required
                  value={typeFormData.libelle}
                  onChange={(e) => setTypeFormData({ ...typeFormData, libelle: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Montant par défaut (FCFA)</label>
                <input
                  type="number"
                  value={typeFormData.montant}
                  onChange={(e) => setTypeFormData({ ...typeFormData, montant: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Observation</label>
                <textarea
                  value={typeFormData.observation}
                  onChange={(e) => setTypeFormData({ ...typeFormData, observation: e.target.value })}
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowTypeForm(false)}>Annuler</button>
                <button type="submit" className="btn-primary">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Prestation Form Modal */}
      {showPrestationForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{selectedPrestation ? 'Modifier la prestation' : 'Nouvelle prestation'}</h2>
            <form onSubmit={handleSavePrestation}>
              <div className="form-group">
                <label>Membre *</label>
                <select
                  required
                  value={prestationFormData.id_membre}
                  onChange={(e) => setPrestationFormData({ ...prestationFormData, id_membre: e.target.value })}
                >
                  <option value="">Sélectionner un membre</option>
                  {membres.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.numero_membre} - {m.nom} {m.prenom}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Type de prestation *</label>
                <select
                  required
                  value={prestationFormData.type_prestation}
                  onChange={(e) => setPrestationFormData({ ...prestationFormData, type_prestation: e.target.value })}
                >
                  <option value="Décès">Décès</option>
                  <option value="Aide">Aide</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Montant (FCFA) *</label>
                  <input
                    type="number"
                    required
                    value={prestationFormData.montant}
                    onChange={(e) => setPrestationFormData({ ...prestationFormData, montant: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    required
                    value={prestationFormData.date_prestation}
                    onChange={(e) => setPrestationFormData({ ...prestationFormData, date_prestation: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={prestationFormData.description}
                  onChange={(e) => setPrestationFormData({ ...prestationFormData, description: e.target.value })}
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowPrestationForm(false)}>Annuler</button>
                <button type="submit" className="btn-primary">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Depense Form Modal */}
      {showDepenseForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{selectedDepense ? 'Modifier la dépense' : 'Nouvelle dépense'}</h2>
            <form onSubmit={handleSaveDepense}>
              <div className="form-group">
                <label>Libellé *</label>
                <input
                  type="text"
                  required
                  value={depenseFormData.libelle}
                  onChange={(e) => setDepenseFormData({ ...depenseFormData, libelle: e.target.value })}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Montant (FCFA) *</label>
                  <input
                    type="number"
                    required
                    value={depenseFormData.montant}
                    onChange={(e) => setDepenseFormData({ ...depenseFormData, montant: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    required
                    value={depenseFormData.date_depense}
                    onChange={(e) => setDepenseFormData({ ...depenseFormData, date_depense: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={depenseFormData.description}
                  onChange={(e) => setDepenseFormData({ ...depenseFormData, description: e.target.value })}
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowDepenseForm(false)}>Annuler</button>
                <button type="submit" className="btn-primary">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ARAModule;
